import { createStore } from 'vuex';
import createPersistedState from 'vuex-persistedstate';

const OFFLINE_GRACE_MS = 30_000;
const pendingOffline = new Map();

export function cancelPendingOffline() {
  pendingOffline.forEach(clearTimeout);
  pendingOffline.clear();
}

// Exported so a reset can put the store back to what a fresh boot would build:
// anything added here is reset for free.
export const createState = () => ({
  alertMessage: {
    messageType: '',
    messageText: '',
    show: false,
  },
  activeTeam: null,
  modInfo: null,
  activeServer: null,
  // The team's running servers. Written straight through by NavBar, which is
  // also the only thing that reads them -- there is no mutation for either.
  activeServers: null,
  selectedMod: null,
  matchmakingParty: null,
  matchmakingLobby: null,
  matchmakingPartyInvites: [],
  matchmakingContext: { lan_party: null, lan_party_offer: null, creator: null },
  friendPresence: {},
  friendPresenceRevision: 0,
  friendPresenceRevisions: {},
});

export const store = createStore({
  state: createState,

  getters: {
    activeTeam(state) {
      return state.activeTeam;
    },

    selectedGame(state) {
      return state.selectedGame;
    },

    activeServer(state) {
      return state.activeServer;
    },

    modInfo(state) {
      return state.modInfo;
    },

    selectedMod(state) {
      return state.selectedMod;
    },

    matchmakingContext(state) {
      return state.matchmakingContext;
    },

    matchmakingParty(state) {
      return state.matchmakingParty;
    },

    matchmakingQueueEntry(state) {
      return state.matchmakingParty?.queue_entry || null;
    },

    matchmakingLobby(state) {
      return state.matchmakingLobby;
    },

    matchmakingPartyInvites(state) {
      return state.matchmakingPartyInvites;
    },

    friendPresence(state) {
      return state.friendPresence;
    },

    friendPresenceRevision(state) {
      return state.friendPresenceRevision;
    },

    friendPresenceRevisions(state) {
      return state.friendPresenceRevisions;
    },
  },

  mutations: {
    showMessage(state, { messageType, messageText }) {
      state.alertMessage = { messageType, messageText, show: true };
    },

    hideMessage(state) {
      state.alertMessage.show = false;
    },

    setActiveTeam(state, team) {
      state.activeTeam = team;
    },

    setSelectedGame(state, value) {
      state.selectedGame = value;
    },

    setModInfo(state, modInfo) {
      state.modInfo = modInfo;
    },

    setActiveServer(state, server) {
      state.activeServer = server;
    },

    setSelectedMod(state, mod) {
      state.selectedMod = mod;
    },

    setMatchmakingParty(state, party) {
      state.matchmakingParty = party;
    },

    // The LAN party fields of GET /matchmaking/parties: the viewer's open LAN party, a seat
    // offer for a regular player, and the creator a manager runs.
    setMatchmakingContext(state, { lan_party: lanParty, lan_party_offer: offer, creator }) {
      state.matchmakingContext = {
        lan_party: lanParty || null, lan_party_offer: offer || null, creator: creator || null,
      };
    },

    clearMatchmakingOffer(state) {
      state.matchmakingContext = { ...state.matchmakingContext, lan_party_offer: null };
    },

    setMatchmakingLobby(state, lobby) {
      state.matchmakingLobby = lobby;
    },

    clearMatchmakingLobby(state) {
      state.matchmakingLobby = null;
    },

    setMatchmakingPartyInvites(state, invites) {
      state.matchmakingPartyInvites = invites || [];
    },

    addMatchmakingPartyInvite(state, invite) {
      if (state.matchmakingPartyInvites.some((existing) => existing.id === invite.id)) return;
      state.matchmakingPartyInvites.push(invite);
    },

    removeMatchmakingPartyInvite(state, inviteId) {
      state.matchmakingPartyInvites = state.matchmakingPartyInvites.filter((invite) => invite.id !== inviteId);
    },

    setFriendPresence(state, { uuid, online }) {
      state.friendPresence = { ...state.friendPresence, [uuid]: online };
      state.friendPresenceRevision += 1;
      state.friendPresenceRevisions = { ...state.friendPresenceRevisions, [uuid]: state.friendPresenceRevision };
    },

    mergeFriendPresence(state, presence) {
      state.friendPresence = { ...state.friendPresence, ...presence };
    },

    clearFriendPresence(state) {
      cancelPendingOffline();
      state.friendPresence = {};
      state.friendPresenceRevisions = {};
    },

    // Refreshes the active team from a fresh /me. It also set activeServers from
    // `team.servers`, which /me has never carried -- one `undefined` per team,
    // enough for the NavBar popover to count and render them.
    updateActiveTeamData(state, auth) {
      auth.fetch().then(() => {
        const user = auth.user();
        const activeTeam = user.teams.find((team) => team.id === state.activeTeam.id);
        state.activeTeam = activeTeam;
      });
    },
  },
  actions: {
    receiveFriendPresence({ commit }, { uuid, online }) {
      clearTimeout(pendingOffline.get(uuid));
      pendingOffline.delete(uuid);
      if (online) {
        commit('setFriendPresence', { uuid, online });
        return;
      }
      pendingOffline.set(uuid, setTimeout(() => {
        pendingOffline.delete(uuid);
        commit('setFriendPresence', { uuid, online: false });
      }, OFFLINE_GRACE_MS));
    },
  },
  plugins: [createPersistedState({ storage: window.sessionStorage })],
});
